import { useEffect, useMemo, useState } from 'react';
import {
  OPPORTUNITY_STAGES,
  OPPORTUNITY_STAGE_LABELS,
  type CwOpportunityStageAliasDto,
  type OpportunityStage,
} from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwOpportunityStageAliases, useSetCwOpportunityStageAliases } from './api.js';

interface AliasRow {
  id: string;
  cwStageName: string;
  stage: OpportunityStage | '';
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `opp-stage-alias-${aliasSeq}`;
}

interface OpportunityStageAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise opportunity stage (a dropdown) onto a PSA opportunity stage. On
 * the opportunities sync, a CW opportunity's stage sets the synced opportunity's stage
 * through these rows; an opportunity whose stage has no row keeps its default/previous
 * stage (the sync never fails on a missing stage alias).
 */
export function OpportunityStageAliasEditor({
  isOpen,
}: OpportunityStageAliasEditorProps): JSX.Element {
  const configQuery = useCwOpportunityStageAliases(isOpen);
  const saveAliases = useSetCwOpportunityStageAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwStageName: alias.cwStageName,
        stage: alias.stage,
      })),
    );
  }, [config]);

  const discoveredStages = config?.discoveredStages ?? [];
  const live = config?.live ?? false;

  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwStageName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredStages.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };
  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };
  const addRow = (cwStageName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwStageName, stage: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwOpportunityStageAliasDto[] = rows
      .filter((row): row is AliasRow & { stage: OpportunityStage } =>
        Boolean(row.cwStageName.trim() && row.stage),
      )
      .map((row) => ({ cwStageName: row.cwStageName.trim(), stage: row.stage }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-opportunity-stages';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Opportunity stage from ConnectWise stage</h3>
        <p className="mt-1 text-sm text-slate-500">
          Map each ConnectWise opportunity stage to a PSA opportunity stage below. On sync, an
          opportunity&apos;s ConnectWise stage sets its PSA stage — an opportunity whose stage has
          no row here keeps its default/previous stage.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading opportunity stages…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load opportunity stages.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise opportunity stages loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise opportunity stages discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredStages.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the stage aliases. Make sure each row targets a valid opportunity
              stage.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No stage aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise stage</th>
                  <th className="py-2 pr-3 font-medium">PSA opportunity stage</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwStageName}
                        placeholder="e.g. Quoted"
                        onChange={(e) => updateRow(row.id, { cwStageName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.stage}
                        onChange={(e) =>
                          updateRow(row.id, { stage: e.target.value as OpportunityStage | '' })
                        }
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select stage —</option>
                        {OPPORTUNITY_STAGES.map((stage) => (
                          <option key={stage} value={stage}>
                            {OPPORTUNITY_STAGE_LABELS[stage]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save stages
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
